import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { 
  Tags, Plus, Pencil, Trash2, GripVertical,
  ChevronUp, ChevronDown, Apple, BookOpen, CheckSquare, Square
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestioneCategorie = ({ embedded = false, onCategorieChange = null }) => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tipo') || 'ingredienti';
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [categorieIngredienti, setCategorieIngredienti] = useState([]);
  const [categorieRicette, setCategorieRicette] = useState([]);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formNome, setFormNome] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Selezione multipla
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Reset selezione quando cambia tab
  useEffect(() => {
    setSelectedItems([]);
  }, [activeTab]);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente?.id]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    
    try {
      const [ingRes, ricRes] = await Promise.all([
        axios.get(`${API}/categorie-ingredienti?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categorie-ricette?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() })
      ]);
      
      setCategorieIngredienti(ingRes.data);
      setCategorieRicette(ricRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle categorie");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    setEditItem(item);
    setFormNome(item?.nome || "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formNome.trim()) {
      toast.error("Nome categoria obbligatorio");
      return;
    }

    setSaving(true);
    const endpoint = activeTab === 'ingredienti' ? 'categorie-ingredienti' : 'categorie-ricette';
    
    try {
      if (editItem) {
        await axios.put(
          `${API}/${endpoint}/${editItem.id}?nome=${encodeURIComponent(formNome)}`,
          {},
          { headers: getAuthHeaders() }
        );
        toast.success("Categoria aggiornata");
      } else {
        await axios.post(
          `${API}/${endpoint}`,
          { locale_id: localeCorrente.id, nome: formNome },
          { headers: getAuthHeaders() }
        );
        toast.success("Categoria creata");
      }
      
      setDialogOpen(false);
      fetchData();
      if (onCategorieChange) onCategorieChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!await confirm(`Sei sicuro di voler eliminare "${item.nome}"?`)) return;

    const endpoint = activeTab === 'ingredienti' ? 'categorie-ingredienti' : 'categorie-ricette';
    
    try {
      await axios.delete(`${API}/${endpoint}/${item.id}`, { headers: getAuthHeaders() });
      toast.success("Categoria eliminata");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Gestione selezione
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const currentCategorie = activeTab === 'ingredienti' ? categorieIngredienti : categorieRicette;
    if (selectedItems.length === currentCategorie.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentCategorie.map(c => c.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;
    
    const tipoLabel = activeTab === 'ingredienti' ? 'categorie ingredienti' : 'categorie ricette';
    if (!await confirm(`Sei sicuro di voler eliminare ${selectedItems.length} ${tipoLabel}?`)) return;

    const endpoint = activeTab === 'ingredienti' ? 'categorie-ingredienti' : 'categorie-ricette';
    
    setDeleting(true);
    try {
      await Promise.all(
        selectedItems.map(id => 
          axios.delete(`${API}/${endpoint}/${id}`, { headers: getAuthHeaders() })
        )
      );
      toast.success(`${selectedItems.length} categorie eliminate`);
      setSelectedItems([]);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione di alcune categorie");
    } finally {
      setDeleting(false);
    }
  };

  const currentCategorie = activeTab === 'ingredienti' ? categorieIngredienti : categorieRicette;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "page-container"} data-testid="gestione-categorie-page">
      {/* Header - nascosto in modalità embedded */}
      {!embedded && (
        <div className="page-header">
          <div>
            <h1 className="page-title">Gestione Categorie</h1>
            <p className="page-subtitle">Organizza le categorie di ingredienti e ricette</p>
          </div>
          <div className="flex gap-2">
            {selectedItems.length > 0 && (
              <Button 
                onClick={handleDeleteSelected}
                variant="destructive"
                disabled={deleting}
                data-testid="delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina {selectedItems.length} selezionate
              </Button>
            )}
            <Button onClick={() => openDialog()} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Categoria
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {selectedItems.length > 0 && (
              <Button 
                onClick={handleDeleteSelected}
                variant="destructive"
                size="sm"
                disabled={deleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina {selectedItems.length}
              </Button>
            )}
          </div>
          <Button onClick={() => openDialog()} size="sm" className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Nuova Categoria
          </Button>
        </div>
      )}

      {/* Selezione rapida */}
      {currentCategorie.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <Checkbox
            checked={selectedItems.length === currentCategorie.length && currentCategorie.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-checkbox"
          />
          <span className="text-sm text-muted-foreground">
            {selectedItems.length === 0 
              ? "Seleziona tutto" 
              : `${selectedItems.length} di ${currentCategorie.length} selezionate`}
          </span>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="tabs-pill-list">
          <TabsTrigger value="ingredienti" className="flex items-center gap-2">
            <Apple className="h-4 w-4" />
            Ingredienti
          </TabsTrigger>
          <TabsTrigger value="ricette" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Ricette
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ingredienti" className="mt-4">
          <CategorieList 
            categorie={categorieIngredienti}
            onEdit={openDialog}
            onDelete={handleDelete}
            accentColor="lime"
            selectedItems={selectedItems}
            onToggleSelect={toggleSelectItem}
          />
        </TabsContent>

        <TabsContent value="ricette" className="mt-4">
          <CategorieList 
            categorie={categorieRicette}
            onEdit={openDialog}
            onDelete={handleDelete}
            accentColor="purple"
            selectedItems={selectedItems}
            onToggleSelect={toggleSelectItem}
          />
        </TabsContent>
      </Tabs>

      {/* Dialog Crea/Modifica */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Tags className={`h-5 w-5 ${activeTab === 'ingredienti' ? 'text-lime-500' : 'text-purple-500'}`} />
              {editItem ? "Modifica Categoria" : "Nuova Categoria"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nome Categoria *</Label>
              <Input
                value={formNome}
                onChange={(e) => setFormNome(e.target.value)}
                placeholder="es. Latticini, Salse..."
                className="bg-card border-border text-foreground"
                autoFocus
              />
            </div>

            <div className="text-xs text-muted-foreground">
              Tipo: {activeTab === 'ingredienti' ? 'Categoria Ingredienti' : 'Categoria Ricette'}
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving} 
                className={`flex-1 ${activeTab === 'ingredienti' ? 'bg-lime-500 hover:bg-lime-400' : 'bg-purple-500 hover:bg-purple-400'} text-white font-bold`}
              >
                {saving ? "Salvataggio..." : (editItem ? "Salva" : "Crea")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente Lista Categorie
const CategorieList = ({ categorie, onEdit, onDelete, accentColor, selectedItems, onToggleSelect }) => {
  if (categorie.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Tags className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nessuna categoria trovata</p>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {categorie.map((item, idx) => (
        <div
          key={item.id}
          className={`flex items-center gap-3 p-4 bg-card rounded-xl border border-border hover:bg-muted transition-colors ${selectedItems.includes(item.id) ? 'ring-2 ring-primary bg-primary/5' : ''}`}
        >
          {/* Checkbox */}
          <Checkbox
            checked={selectedItems.includes(item.id)}
            onCheckedChange={() => onToggleSelect(item.id)}
            data-testid={`checkbox-${item.id}`}
          />
          
          {/* Order Number */}
          <div className={`w-8 h-8 rounded-full ${accentColor === 'lime' ? 'bg-lime-400/20 text-lime-600' : 'bg-purple-500/20 text-purple-500'} flex items-center justify-center text-sm font-bold flex-shrink-0`}>
            {idx + 1}
          </div>
          
          {/* Nome */}
          <div className="flex-1 min-w-0">
            <h3 className="text-foreground font-medium truncate">{item.nome}</h3>
          </div>
          
          {/* Actions */}
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onEdit(item)}
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onDelete(item)}
              className="h-9 w-9 text-red-400 hover:text-red-600 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GestioneCategorie;